import React, { useState, useEffect, useRef } from "react";
import {
  collection, addDoc, onSnapshot, query, where, orderBy,
  limit, serverTimestamp, updateDoc, deleteDoc, doc, getDoc, getDocs, Timestamp, writeBatch,
} from "firebase/firestore";
import EditableText from "./EditableText";
import { db } from "../firebase";
import { connect } from "react-redux";
import { useParams, Link } from "react-router-dom";
import genericProfilePic from "../genericprofilepic.png";

const BOOK_TO_APIBOOK = {
  "Genesis": "gen", "Exodus": "exo", "Leviticus": "lev", "Numbers": "num",
  "Deuteronomy": "deu", "Joshua": "jos", "Judges": "jdg", "1 Samuel": "1sa",
  "2 Samuel": "2sa", "1 Kings": "1ki", "2 Kings": "2ki", "Isaiah": "isa",
  "Jeremiah": "jer", "Ezekiel": "ezk", "Hosea": "hos", "Joel": "jol",
  "Amos": "amo", "Obadiah": "oba", "Jonah": "jon", "Micah": "mic",
  "Nahum": "nam", "Habakkuk": "hab", "Zephaniah": "zep", "Haggai": "hag",
  "Zechariah": "zec", "Malachi": "mal", "Psalms": "psa", "Proverbs": "pro",
  "Job": "job", "Song of Songs": "sng", "Ruth": "rut", "Lamentations": "lam",
  "Ecclesiastes": "ecc", "Esther": "est", "Daniel": "dan", "Ezra": "ezr",
  "Nehemiah": "neh", "1 Chronicles": "1ch", "2 Chronicles": "2ch",
  "Matthew": "mat", "Mark": "mrk", "Luke": "luk", "John": "jhn",
  "Acts": "act", "Romans": "rom", "1 Corinthians": "1co", "2 Corinthians": "2co",
  "Galatians": "gal", "Ephesians": "eph", "Philippians": "php", "Colossians": "col",
  "1 Thessalonians": "1th", "2 Thessalonians": "2th", "1 Timothy": "1ti",
  "2 Timothy": "2ti", "Titus": "tit", "Philemon": "phm", "Hebrews": "heb",
  "James": "jas", "1 Peter": "1pe", "2 Peter": "2pe", "1 John": "1jn",
  "2 John": "2jn", "3 John": "3jn", "Jude": "jud", "Revelation": "rev",
};

function getScriptureLink(c) {
  if (c.type === "torah") return "/torah-portion";
  if (c.type === "haftarah") return "/haftarah-portion";
  if (c.type === "gospel") return "/new-testament-portion";
  const apibook = BOOK_TO_APIBOOK[c.book];
  return apibook ? `/${apibook}${c.chapter}` : "/read";
}

function getScriptureLabel(c) {
  if (c.type === "torah") return `Torah: ${c.portion}`;
  if (c.type === "haftarah") return `Haftarah: ${c.portion}`;
  if (c.type === "gospel") return `New Testament: ${c.portion}`;
  return `${c.book} ${c.chapter}`;
}

function CommentaryCard({ c }) {
  return (
    <div style={{ background: "#F8FBFF", borderRadius: "12px", padding: "14px", border: "1.5px solid #DBEAFE" }}>
      <p style={{ fontFamily: "Hebrew", fontSize: "1.1rem", color: "#888", margin: "0 0 6px" }}>
        {getScriptureLabel(c)}
      </p>
      <p style={{ fontFamily: "Hebrew", fontSize: "1.4rem", color: "#1A1A2E", margin: "0 0 12px", lineHeight: "1.6" }}>
        {c.commentary}
      </p>
      <Link to={getScriptureLink(c)}>
        <button style={{
          padding: "10px 18px",
          borderRadius: "12px",
          border: "1.5px solid #0D3B8C",
          background: "white",
          color: "#0D3B8C",
          fontFamily: "Hebrew",
          fontSize: "1.1rem",
          cursor: "pointer",
          lineHeight: "1.5",
          whiteSpace: "normal",
          textAlign: "center",
          display: "inline-block",
        }}>
          Read the Scripture &amp; Join the Discussion
        </button>
      </Link>
    </div>
  );
}

function GroupDetail({ first_name, last_name, profile_pic_url }) {
  const { groupId } = useParams();
  const userId = localStorage.getItem("user_id");

  const [group, setGroup] = useState(null);
  const [membership, setMembership] = useState(null);
  const [loadingMembership, setLoadingMembership] = useState(true);
  const [members, setMembers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [activeTab, setActiveTab] = useState("discussion");

  const [posts, setPosts] = useState([]);
  const [postComments, setPostComments] = useState([]);
  const [postText, setPostText] = useState("");
  const [commentTexts, setCommentTexts] = useState({});
  const [openCommentFor, setOpenCommentFor] = useState(null);

  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const chatEndRef = useRef(null);

  const [commentaries, setCommentaries] = useState([]);
  const [expandedMember, setExpandedMember] = useState(null);
  const [expandedPage, setExpandedPage] = useState(0);

  useEffect(() => {
    getDoc(doc(db, "groups", groupId)).then(snap => {
      if (snap.exists()) setGroup({ id: snap.id, ...snap.data() });
    });

    const unsubMembership = onSnapshot(
      query(
        collection(db, "group_members"),
        where("group_id", "==", groupId),
        where("user_id", "==", userId)
      ),
      snap => {
        setMembership(snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() });
        setLoadingMembership(false);
      }
    );

    return () => unsubMembership();
  }, [groupId, userId]);

  useEffect(() => {
    if (!membership || membership.status !== "approved") return;

    const unsubMembers = onSnapshot(
      query(collection(db, "group_members"), where("group_id", "==", groupId), where("status", "==", "approved")),
      snap => setMembers(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );

    const unsubPending = onSnapshot(
      query(collection(db, "group_members"), where("group_id", "==", groupId), where("status", "==", "pending")),
      snap => setPendingRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );

    const unsubPosts = onSnapshot(
      query(collection(db, "group_posts"), where("group_id", "==", groupId)),
      snap => {
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        docs.sort((a, b) => (b.created_at?.toMillis?.() || 0) - (a.created_at?.toMillis?.() || 0));
        setPosts(docs);
      }
    );

    const unsubPostComments = onSnapshot(
      query(collection(db, "group_post_comments"), where("group_id", "==", groupId)),
      snap => {
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        docs.sort((a, b) => (a.created_at?.toMillis?.() || 0) - (b.created_at?.toMillis?.() || 0));
        setPostComments(docs);
      }
    );

    const unsubChat = onSnapshot(
      query(collection(db, "group_chat"), where("group_id", "==", groupId)),
      snap => {
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        docs.sort((a, b) => (a.created_at?.toMillis?.() || 0) - (b.created_at?.toMillis?.() || 0));
        setChatMessages(docs.slice(-100));
      }
    );

    return () => {
      unsubMembers(); unsubPending(); unsubPosts(); unsubPostComments(); unsubChat();
    };
  }, [membership, groupId]);

  useEffect(() => {
    if (members.length === 0) return;
    const memberIds = members.map(m => m.user_id).slice(0, 30);
    const q = (col) => getDocs(query(collection(db, col), where("user_id", "in", memberIds)));
    Promise.all([q("commentaries"), q("torahCommentaries"), q("haftarahCommentaries"), q("gospelCommentaries")])
      .then(([bible, torah, haftarah, gospel]) => {
        const all = [
          ...bible.docs.map(d => ({ id: d.id, type: "bible", ...d.data() })),
          ...torah.docs.map(d => ({ id: d.id, type: "torah", ...d.data() })),
          ...haftarah.docs.map(d => ({ id: d.id, type: "haftarah", ...d.data() })),
          ...gospel.docs.map(d => ({ id: d.id, type: "gospel", ...d.data() })),
        ];
        all.sort((a, b) => (b.created_at?.toMillis?.() || 0) - (a.created_at?.toMillis?.() || 0));
        setCommentaries(all);
      });
  }, [members]);

  useEffect(() => {
    if (activeTab === "chat") {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages, activeTab]);

  const postDiscussion = async () => {
    const text = postText.trim();
    if (!text) return;
    setPostText("");
    await addDoc(collection(db, "group_posts"), {
      group_id: groupId,
      user_id: userId,
      first_name: first_name || "",
      last_name: last_name || "",
      profile_pic_url: profile_pic_url || null,
      post: text,
      created_at: Timestamp.fromDate(new Date()),
    });
  };

  const postComment = async (postId) => {
    const text = (commentTexts[postId] || "").trim();
    if (!text) return;
    await addDoc(collection(db, "group_post_comments"), {
      group_id: groupId,
      post_id: postId,
      user_id: userId,
      first_name: first_name || "",
      last_name: last_name || "",
      profile_pic_url: profile_pic_url || null,
      comment: text,
      created_at: Timestamp.fromDate(new Date()),
    });
    setCommentTexts(prev => ({ ...prev, [postId]: "" }));
    setOpenCommentFor(null);
  };

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text) return;
    setChatInput("");
    await addDoc(collection(db, "group_chat"), {
      group_id: groupId,
      user_id: userId,
      first_name: first_name || "",
      last_name: last_name || "",
      profile_pic_url: profile_pic_url || null,
      message: text,
      created_at: Timestamp.fromDate(new Date()),
    });
  };

  const handleChatKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendChat();
    }
  };

  // Posts and replies update live through onSnapshot, so no local state edits needed
  const editPost = (postId, text) =>
    updateDoc(doc(db, "group_posts", postId), { post: text, edited_at: serverTimestamp() });

  const deletePost = async (postId) => {
    const batch = writeBatch(db);
    postComments.filter(c => c.post_id === postId).forEach(c => batch.delete(doc(db, "group_post_comments", c.id)));
    batch.delete(doc(db, "group_posts", postId));
    await batch.commit();
  };

  const editReply = (commentId, text) =>
    updateDoc(doc(db, "group_post_comments", commentId), { comment: text, edited_at: serverTimestamp() });

  const deleteReply = (commentId) => deleteDoc(doc(db, "group_post_comments", commentId));

  const approveMember = (memberId) =>
    updateDoc(doc(db, "group_members", memberId), { status: "approved" });

  const denyMember = (memberId) =>
    deleteDoc(doc(db, "group_members", memberId));

  const formatTime = (ts) => {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleTimeString("en-us", { hour: "numeric", minute: "2-digit" });
  };

  const expandedCommentaries = expandedMember ? commentaries.filter(c => c.user_id === expandedMember) : [];
  const EXPANDED_PAGE_SIZE = 10;
  const expandedTotalPages = Math.ceil(expandedCommentaries.length / EXPANDED_PAGE_SIZE);
  const expandedPageItems = expandedCommentaries.slice(expandedPage * EXPANDED_PAGE_SIZE, (expandedPage + 1) * EXPANDED_PAGE_SIZE);

  const formatDate = (ts) => {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-us", { month: "short", day: "numeric", year: "numeric" });
  };

  const boxStyle = {
    background: "rgba(255,255,255,0.88)",
    borderRadius: "16px",
    width: "85%",
    maxWidth: "640px",
    margin: "0 auto 20px",
    boxShadow: "0 2px 10px rgba(13,59,140,0.1)",
  };

  if (loadingMembership) {
    return (
      <div style={{ minHeight: "100vh", background: "#F0F7FF" }}>
        <br /><br />
        <p style={{ fontFamily: "Hebrew", fontSize: "1.4rem", textAlign: "center" }}>Loading…</p>
      </div>
    );
  }

  if (!membership || membership.status === "pending") {
    return (
      <div style={{ minHeight: "100vh", background: "#F0F7FF", paddingBottom: "40px" }}>
        <br /><br />
        <h2>{group?.name || "Group"}</h2>
        {group?.description && (
          <p style={{ fontFamily: "Hebrew", fontSize: "1.4rem", color: "#444", textAlign: "center", margin: "0 auto 20px", maxWidth: "600px", padding: "0 20px" }}>
            {group.description}
          </p>
        )}
        <div style={{ ...boxStyle, padding: "24px 20px", textAlign: "center" }}>
          {!membership ? (
            <p style={{ fontFamily: "Hebrew", fontSize: "1.5rem", color: "#0D3B8C", margin: "0 0 16px" }}>
              You are not a member of this group. Request to join from the Groups page.
            </p>
          ) : (
            <p style={{ fontFamily: "Hebrew", fontSize: "1.5rem", color: "#F59E0B", margin: "0 0 16px" }}>
              Your membership request is pending admin approval.
            </p>
          )}
          <Link to="/groups">
            <button className="brownroundbutton" style={{ cursor: "pointer" }}>← Back to Groups</button>
          </Link>
        </div>
      </div>
    );
  }

  const isAdmin = membership?.role === "admin";
  const tabs = ["discussion", "chat", "commentary", "members"];

  return (
    <div style={{ minHeight: "100vh", background: "#F0F7FF", paddingBottom: "40px" }}>
      <br /><br />
      <h2>{group?.name}</h2>
      {group?.description && (
        <p style={{ fontFamily: "Hebrew", fontSize: "1.4rem", color: "#444", textAlign: "center", margin: "0 auto 8px", maxWidth: "600px", padding: "0 20px" }}>
          {group.description}
        </p>
      )}
      <p style={{ fontFamily: "Hebrew", fontSize: "1.2rem", color: "#888", textAlign: "center", margin: "0 0 20px" }}>
        {members.length} member{members.length !== 1 ? "s" : ""}
        {isAdmin && <span style={{ marginLeft: "8px", color: "#F5B300", fontWeight: "bold" }}>· Admin</span>}
      </p>

      {/* Tab bar */}
      <div style={{ display: "flex", justifyContent: "center", gap: "8px", flexWrap: "wrap", marginBottom: "20px", padding: "0 10px" }}>
        {tabs.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: "8px 18px",
              borderRadius: "20px",
              border: "2px solid #0D3B8C",
              background: activeTab === tab ? "#0D3B8C" : "white",
              color: activeTab === tab ? "white" : "#0D3B8C",
              fontFamily: "Hebrew",
              fontSize: "1.3rem",
              cursor: "pointer",
              fontWeight: activeTab === tab ? "bold" : "normal",
              textTransform: "capitalize",
              position: "relative",
            }}
          >
            {tab}
            {tab === "members" && isAdmin && pendingRequests.length > 0 && (
              <span style={{ background: "#EF4444", color: "white", borderRadius: "50%", padding: "1px 6px", fontSize: "1rem", marginLeft: "6px", fontWeight: "bold" }}>
                {pendingRequests.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Discussion ── */}
      {activeTab === "discussion" && (
        <div style={{ ...boxStyle }}>
          <div style={{ padding: "14px 20px", borderBottom: "2px solid #DBEAFE" }}>
            <p style={{ fontFamily: "Hebrew", fontSize: "1.6rem", fontWeight: "bold", color: "#0D3B8C", margin: 0 }}>Discussion</p>
          </div>
          <div style={{ padding: "16px 20px", borderBottom: "2px solid #DBEAFE" }}>
            <textarea
              value={postText}
              onChange={e => setPostText(e.target.value)}
              placeholder="Start a conversation…"
              rows={3}
              style={{ width: "100%", boxSizing: "border-box", padding: "10px 14px", fontSize: "15px", borderRadius: "12px", border: "2px solid #DBEAFE", fontFamily: "Hebrew", resize: "none", outline: "none", marginBottom: "8px" }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                className="brownroundbutton"
                onClick={postDiscussion}
                disabled={!postText.trim()}
                style={{ cursor: "pointer" }}
              >
                Post
              </button>
            </div>
          </div>
          <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: "20px" }}>
            {posts.length === 0 && (
              <p style={{ fontFamily: "Hebrew", fontSize: "1.4rem", color: "#bbb", fontStyle: "italic", textAlign: "center" }}>
                No posts yet — start the conversation!
              </p>
            )}
            {posts.map(post => (
              <div key={post.id} style={{ borderBottom: "1.5px solid #DBEAFE", paddingBottom: "16px" }}>
                <div style={{ display: "flex", gap: "10px", marginBottom: "8px" }}>
                  <img
                    src={post.profile_pic_url || genericProfilePic}
                    alt={post.first_name}
                    style={{ width: "36px", height: "36px", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                  />
                  <div>
                    <p style={{ fontFamily: "Hebrew", fontSize: "1.3rem", fontWeight: "bold", color: "#0D3B8C", margin: 0 }}>
                      {post.first_name} {post.last_name}
                    </p>
                    <p style={{ fontFamily: "Hebrew", fontSize: "1.1rem", color: "#999", margin: 0 }}>{formatDate(post.created_at)}</p>
                  </div>
                </div>
                <EditableText
                  text={post.post}
                  edited={!!post.edited_at}
                  compact
                  textClassName=""
                  textStyle={{ fontFamily: "Hebrew", fontSize: "1.5rem", color: "#1A1A2E", margin: "0 0 6px", lineHeight: "1.6" }}
                  canEdit={post.user_id === userId}
                  canDelete={post.user_id === userId || isAdmin}
                  onSave={({ text }) => editPost(post.id, text)}
                  onDelete={() => deletePost(post.id)}
                />

                {postComments.filter(c => c.post_id === post.id).map(comment => (
                  <div key={comment.id} style={{ display: "flex", gap: "8px", marginLeft: "20px", marginBottom: "8px" }}>
                    <img
                      src={comment.profile_pic_url || genericProfilePic}
                      alt={comment.first_name}
                      style={{ width: "28px", height: "28px", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                    />
                    <div style={{ background: "#F0F7FF", borderRadius: "12px", padding: "8px 12px", flex: 1 }}>
                      <p style={{ fontFamily: "Hebrew", fontSize: "1.2rem", fontWeight: "bold", color: "#0D3B8C", margin: "0 0 2px" }}>
                        {comment.first_name} {comment.last_name}
                      </p>
                      <EditableText
                        text={comment.comment}
                        edited={!!comment.edited_at}
                        itemLabel="reply"
                        compact
                        textClassName=""
                        textStyle={{ fontFamily: "Hebrew", fontSize: "1.3rem", color: "#1A1A2E", margin: 0 }}
                        canEdit={comment.user_id === userId}
                        canDelete={comment.user_id === userId || isAdmin || post.user_id === userId}
                        onSave={({ text }) => editReply(comment.id, text)}
                        onDelete={() => deleteReply(comment.id)}
                      />
                    </div>
                  </div>
                ))}

                {openCommentFor === post.id ? (
                  <div style={{ marginLeft: "20px", marginTop: "8px" }}>
                    <textarea
                      value={commentTexts[post.id] || ""}
                      onChange={e => setCommentTexts(prev => ({ ...prev, [post.id]: e.target.value }))}
                      placeholder="Write a reply…"
                      rows={2}
                      style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", fontSize: "14px", borderRadius: "10px", border: "2px solid #DBEAFE", fontFamily: "Hebrew", resize: "none", outline: "none", marginBottom: "6px" }}
                    />
                    <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                      <button
                        onClick={() => { setOpenCommentFor(null); setCommentTexts(prev => ({ ...prev, [post.id]: "" })); }}
                        style={{ padding: "6px 14px", borderRadius: "12px", border: "2px solid #888", background: "#888", color: "white", fontFamily: "Hebrew", fontSize: "1.2rem", cursor: "pointer" }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => postComment(post.id)}
                        disabled={!(commentTexts[post.id] || "").trim()}
                        style={{ padding: "6px 14px", borderRadius: "12px", border: "none", background: "#0D3B8C", color: "white", fontFamily: "Hebrew", fontSize: "1.2rem", cursor: "pointer" }}
                      >
                        Reply
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setOpenCommentFor(post.id)}
                    style={{ marginLeft: "20px", padding: "4px 12px", borderRadius: "12px", border: "1.5px solid #BFDBFE", background: "white", color: "#0D3B8C", fontFamily: "Hebrew", fontSize: "1.2rem", cursor: "pointer" }}
                  >
                    Reply
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Chat ── */}
      {activeTab === "chat" && (
        <div style={{ ...boxStyle, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ padding: "14px 20px", borderBottom: "2px solid #DBEAFE" }}>
            <p style={{ fontFamily: "Hebrew", fontSize: "1.6rem", fontWeight: "bold", color: "#0D3B8C", margin: 0 }}>Group Chat</p>
          </div>
          <div style={{ height: "400px", overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: "12px" }}>
            {chatMessages.length === 0 && (
              <p style={{ fontFamily: "Hebrew", fontSize: "1.4rem", color: "#bbb", fontStyle: "italic", textAlign: "center", marginTop: "80px" }}>
                No messages yet — say hello! 👋
              </p>
            )}
            {chatMessages.map(msg => {
              const isMe = msg.user_id === userId;
              return (
                <div key={msg.id} style={{ display: "flex", gap: "10px", flexDirection: isMe ? "row-reverse" : "row", alignItems: "flex-start" }}>
                  <img
                    src={msg.profile_pic_url || genericProfilePic}
                    alt={msg.first_name}
                    style={{ width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                  />
                  <div style={{ maxWidth: "72%" }}>
                    <p style={{ fontFamily: "Hebrew", fontSize: "1.2rem", color: "#999", margin: "0 0 3px", textAlign: isMe ? "right" : "left" }}>
                      {isMe ? "You" : `${msg.first_name} ${msg.last_name}`}
                      {msg.created_at && <span style={{ marginLeft: "6px", fontSize: "1.1rem" }}>{formatTime(msg.created_at)}</span>}
                    </p>
                    <div style={{
                      background: isMe ? "#0D3B8C" : "#F0F7FF",
                      color: isMe ? "white" : "#1A1A2E",
                      borderRadius: isMe ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
                      padding: "10px 14px",
                      fontFamily: "Hebrew",
                      fontSize: "1.5rem",
                      lineHeight: "1.5",
                      border: isMe ? "none" : "1.5px solid #DBEAFE",
                      wordBreak: "break-word",
                    }}>
                      {msg.message}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={chatEndRef} />
          </div>
          <div style={{ padding: "12px 16px", borderTop: "2px solid #DBEAFE", display: "flex", flexDirection: "column", gap: "8px" }}>
            <textarea
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={handleChatKeyDown}
              placeholder="Type a message…"
              rows={1}
              style={{ width: "100%", boxSizing: "border-box", padding: "10px 14px", fontSize: "15px", borderRadius: "12px", border: "2px solid #DBEAFE", fontFamily: "Hebrew", resize: "none", minHeight: "44px", maxHeight: "120px", lineHeight: "1.5", outline: "none" }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={sendChat} className="brownroundbutton" style={{ cursor: "pointer", margin: 0 }}>
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Commentary ── */}
      {activeTab === "commentary" && (
        <div style={{ ...boxStyle }}>
          <div style={{ padding: "14px 20px", borderBottom: "2px solid #DBEAFE", display: "flex", alignItems: "center", gap: "12px" }}>
            {expandedMember && (
              <button
                onClick={() => { setExpandedMember(null); setExpandedPage(0); }}
                style={{ padding: "6px 14px", borderRadius: "12px", border: "1.5px solid #0D3B8C", background: "white", color: "#0D3B8C", fontFamily: "Hebrew", fontSize: "1.2rem", cursor: "pointer", flexShrink: 0 }}
              >
                ← Back
              </button>
            )}
            <p style={{ fontFamily: "Hebrew", fontSize: "1.6rem", fontWeight: "bold", color: "#0D3B8C", margin: 0 }}>
              {expandedMember
                ? `${members.find(m => m.user_id === expandedMember)?.first_name || ""} ${members.find(m => m.user_id === expandedMember)?.last_name || ""}'s Commentary`
                : "Members' Commentary"
              }
            </p>
          </div>
          <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: "20px" }}>
            {commentaries.length === 0 && (
              <p style={{ fontFamily: "Hebrew", fontSize: "1.4rem", color: "#bbb", fontStyle: "italic", textAlign: "center" }}>
                No commentary from group members yet.
              </p>
            )}

            {/* Expanded member view with pagination */}
            {expandedMember && (
              <>
                {expandedPageItems.map(c => <CommentaryCard key={c.id} c={c} />)}
                {expandedTotalPages > 1 && (
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "12px", marginTop: "4px" }}>
                    <button
                      disabled={expandedPage === 0}
                      onClick={() => setExpandedPage(p => p - 1)}
                      style={{ padding: "6px 14px", borderRadius: "12px", border: "1.5px solid #0D3B8C", background: expandedPage === 0 ? "#f0f0f0" : "white", color: expandedPage === 0 ? "#aaa" : "#0D3B8C", fontFamily: "Hebrew", fontSize: "1.2rem", cursor: expandedPage === 0 ? "default" : "pointer" }}
                    >
                      ← Prev
                    </button>
                    <span style={{ fontFamily: "Hebrew", fontSize: "1.3rem", color: "#666" }}>{expandedPage + 1} / {expandedTotalPages}</span>
                    <button
                      disabled={expandedPage >= expandedTotalPages - 1}
                      onClick={() => setExpandedPage(p => p + 1)}
                      style={{ padding: "6px 14px", borderRadius: "12px", border: "1.5px solid #0D3B8C", background: expandedPage >= expandedTotalPages - 1 ? "#f0f0f0" : "white", color: expandedPage >= expandedTotalPages - 1 ? "#aaa" : "#0D3B8C", fontFamily: "Hebrew", fontSize: "1.2rem", cursor: expandedPage >= expandedTotalPages - 1 ? "default" : "pointer" }}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Summary view: grouped by member, max 5 each */}
            {!expandedMember && members.map(member => {
              const all = commentaries.filter(c => c.user_id === member.user_id);
              if (all.length === 0) return null;
              const visible = all.slice(0, 5);
              const hasMore = all.length > 5;
              return (
                <div key={member.id} style={{ borderBottom: "1.5px solid #DBEAFE", paddingBottom: "20px" }}>
                  <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "14px" }}>
                    <img
                      src={member.profile_pic_url || genericProfilePic}
                      alt={member.first_name}
                      style={{ width: "36px", height: "36px", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                    />
                    <p style={{ fontFamily: "Hebrew", fontSize: "1.4rem", fontWeight: "bold", color: "#0D3B8C", margin: 0 }}>
                      {member.first_name} {member.last_name}
                    </p>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                    {visible.map(c => <CommentaryCard key={c.id} c={c} />)}
                  </div>
                  {hasMore && (
                    <button
                      onClick={() => { setExpandedMember(member.user_id); setExpandedPage(0); }}
                      style={{ marginTop: "14px", padding: "7px 16px", borderRadius: "12px", border: "1.5px solid #0D3B8C", background: "white", color: "#0D3B8C", fontFamily: "Hebrew", fontSize: "1.2rem", cursor: "pointer" }}
                    >
                      See All ({all.length}) →
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Members ── */}
      {activeTab === "members" && (
        <div style={{ ...boxStyle }}>
          <div style={{ padding: "14px 20px", borderBottom: "2px solid #DBEAFE" }}>
            <p style={{ fontFamily: "Hebrew", fontSize: "1.6rem", fontWeight: "bold", color: "#0D3B8C", margin: 0 }}>
              Members ({members.length})
            </p>
          </div>

          {isAdmin && pendingRequests.length > 0 && (
            <div style={{ padding: "16px 20px", borderBottom: "2px solid #DBEAFE", background: "#FFFBEB" }}>
              <p style={{ fontFamily: "Hebrew", fontSize: "1.4rem", fontWeight: "bold", color: "#D97706", margin: "0 0 12px" }}>
                Pending Requests ({pendingRequests.length})
              </p>
              {pendingRequests.map(req => (
                <div key={req.id} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                  <img
                    src={req.profile_pic_url || genericProfilePic}
                    alt={req.first_name}
                    style={{ width: "36px", height: "36px", borderRadius: "50%", objectFit: "cover" }}
                  />
                  <span style={{ fontFamily: "Hebrew", fontSize: "1.4rem", color: "#1A1A2E", flex: 1 }}>
                    {req.first_name} {req.last_name}
                  </span>
                  <button
                    onClick={() => approveMember(req.id)}
                    style={{ padding: "6px 14px", borderRadius: "12px", border: "none", background: "#22C55E", color: "white", fontFamily: "Hebrew", fontSize: "1.2rem", cursor: "pointer" }}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => denyMember(req.id)}
                    style={{ padding: "6px 14px", borderRadius: "12px", border: "none", background: "#EF4444", color: "white", fontFamily: "Hebrew", fontSize: "1.2rem", cursor: "pointer" }}
                  >
                    Deny
                  </button>
                </div>
              ))}
            </div>
          )}

          <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: "12px" }}>
            {members.map(member => (
              <div key={member.id} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <img
                  src={member.profile_pic_url || genericProfilePic}
                  alt={member.first_name}
                  style={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover" }}
                />
                <div style={{ flex: 1 }}>
                  <p style={{ fontFamily: "Hebrew", fontSize: "1.4rem", fontWeight: "bold", color: "#0D3B8C", margin: 0 }}>
                    {member.first_name} {member.last_name}
                    {member.role === "admin" && (
                      <span style={{ marginLeft: "8px", fontSize: "1.1rem", color: "#F5B300", fontWeight: "normal" }}>Admin</span>
                    )}
                  </p>
                </div>
                <Link to={`/user/${member.first_name}${member.last_name}`}>
                  <button style={{ padding: "5px 12px", borderRadius: "12px", border: "1.5px solid #BFDBFE", background: "white", color: "#0D3B8C", fontFamily: "Hebrew", fontSize: "1.2rem", cursor: "pointer" }}>
                    The Latest from {member.first_name}
                  </button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ textAlign: "center", marginTop: "4px" }}>
        <Link to="/groups">
          <button
            className="brownroundbutton"
            style={{ cursor: "pointer", background: "transparent", border: "2px solid #0D3B8C", color: "#0D3B8C" }}
          >
            ← All Groups
          </button>
        </Link>
      </div>
      <br />
    </div>
  );
}

const mapStateToProps = (state) => ({
  first_name: state.first_name,
  last_name: state.last_name,
  profile_pic_url: state.profile_pic_url,
});

export default connect(mapStateToProps, {})(GroupDetail);
